//
export type MessageChannel = "email" | "sms" | "voice" | "note" | "chat";
export type MessageDirection = "inbound" | "outbound";
export type MessageStatus =
  | "draft"
  | "sending"
  | "sent"
  | "delivered"
  | "failed"
  | "read"
  | "bounced";
export type SenderType = "contact" | "user" | "system";
export type SmsKeywordType = "opt_out" | "opt_in";

export interface EmailDetails {
  subject: string;
  from_address: string;
  to_addresses: string[];
  cc_addresses?: string[];
  bcc_addresses?: string[];
  content_html?: string;
  message_id_header?: string | null;
  in_reply_to?: string | null;
  sent_via_quota_fallback?: boolean;
}

export interface SmsDetails {
  from_number: string;
  to_number: string;
  segments_count: number;
}

export interface ManualDetails {
  note_type: "note" | "call_log" | "meeting_note";
}

export interface TranscriptLine {
  speaker: string;
  message: string;
}

export interface NoteDetails {
  type: MessageChannel | "call_transcript";
  date: string;
  content_html: string | null;
  call_transcript_id?: number;
}

// Full transcript data fetched from boompay-api via call_transcript_id
export interface CallTranscriptData {
  agent_name: string | null;
  summary: string | null;
  recording_url: string | null;
  recording_duration_seconds: number | null;
  transcript: TranscriptLine[];
  // Outbound-softphone route only (absent on the AI-agent route). Typed for
  // completeness; the call card decides on `transcript` being non-empty and does
  // not surface a "transcription failed" state of its own.
  transcription_status?: "completed" | "failed" | "unavailable" | null;
}

export interface Attachment {
  id: string;
  filename: string;
  file_url: string;
  file_size_bytes: number;
  mime_type: string | null;
  content_id: string | null;
  is_inline: boolean;
}

export interface ChatDetails {
  application_id?: number;
  client_message_id?: string | null;
  ai_flagged?: boolean; // set by the support agent AI when it flags the message
  sent_as_sms?: boolean; // an SMS copy of this chat was sent to the applicant
  sent_as_sms_to?: string | null; // phone number the SMS copy was sent to
  sent_as_sms_failed_reason?: string | null; // set when the SMS copy could not be sent
  received_via_sms?: boolean; // inbound message arrived as an applicant SMS reply
}

export interface VoiceDetails {
  // communication-service (PR 58) stores and serializes this as a string
  // (DB VARCHAR(64), Prisma String, DTO `call_id: string | null`).
  call_id: string;
  call_status: string;
  duration_seconds: number | null;
  recording_duration: number | null;
  // Expanded projection from communication-service (CRM-811). `recording_url`
  // here is the raw Twilio URL (not browser-playable) — use the boompay-api
  // /recording or /transcript endpoint for playback.
  from_number?: string | null;
  to_number?: string | null;
  // Display labels for the From/To rows of the call card (CRM-811). from_name is
  // the lead's property (fallback: the provisioned number's label), to_name the
  // lead or customer name. Null for a call placed without a lead.
  from_name?: string | null;
  to_name?: string | null;
  recording_url?: string | null;
  started_at?: string | null;
  answered_at?: string | null;
  ended_at?: string | null;
  version?: number;
}

// AI-216: suggested reply, present only on the anchor message of an inbound burst.
export type SuggestedReplyStatus = "ready" | "failed" | "generating";

export interface SuggestedReply {
  status: SuggestedReplyStatus;
  text: string | null;
  channel: "sms" | "email";
  generated_at: string | null;
  failed_reason: string | null;
  metadata: Record<string, unknown> | null;
  covers_message_ids: string[];
}

export interface Message {
  id: string;
  channel: MessageChannel;
  direction: MessageDirection;
  content_plain: string | null;
  sender_type: SenderType;
  sender_id: number | null;
  sender_identifier: string | null;
  status: MessageStatus;
  sms_keyword_type?: SmsKeywordType | null;
  sent_at: string | null;
  created_at: string;
  email_details?: EmailDetails;
  sms_details?: SmsDetails;
  manual_details?: ManualDetails;
  note_details?: NoteDetails;
  chat_details?: ChatDetails;
  voice_details?: VoiceDetails | null;
  attachments: Attachment[];
  failure_reason?: string | null;
  retry_allowed?: boolean;
  retry_disabled_reason?: string | null;
  suggested_reply?: SuggestedReply | null;
  // AI-216: true on outbound messages sent from an accepted AI suggestion.
  from_ai_suggestion?: boolean;
  // True on outbound messages the AI leasing agent auto-sent (auto_response mode).
  ai_auto_sent?: boolean;
}

export interface ConversationContact {
  id: string;
  display_name: string;
  primary_email: string | null;
  primary_phone: string | null;
}

export interface Conversation {
  id: string;
  contact_id: number;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_message_channel: MessageChannel | null;
  last_contacted_via: string | null;
  unread_count: number;
  is_active: boolean;
  last_message_ai_flagged?: boolean;
  ai_suggestions_enabled?: boolean;
  // False when the number the contact last texted has no active AI leasing
  // agent — the AI-suggestions switch is hidden and no suggestions generate.
  ai_suggestions_available?: boolean;
  // The leasing agent's current, schedule-driven message mode — labels the
  // switch "(auto-response)" vs "(suggest)". null when no agent identity
  // resolves.
  message_mode?: "suggest" | "auto_response" | null;
  created_at?: string;
  // Hydrated by communication-service from boompay-api; null when the
  // contact could not be resolved — render "Unknown Contact" fallbacks.
  contact?: ConversationContact | null;
}

export interface Pagination {
  total: number;
  page: number;
  per_page: number;
}

// API Response types
export interface ConversationsResponse {
  conversations: Conversation[];
  pagination: Pagination;
}

export interface ConversationResponse {
  conversation: Conversation;
}

export interface UnreadConversationsCountResponse {
  unread_conversations: number;
}

export interface SendOptionsResponse {
  unrestricted: boolean;
  emails: string[];
  phones: string[];
  property_ids: number[];
}

export interface MessagesResponse {
  messages: Message[];
  has_more: boolean;
  next_cursor?: string;
  has_more_before?: boolean;
  before_cursor?: string | null;
  has_more_after?: boolean;
  after_cursor?: string | null;
}

export interface SearchContactItem {
  contact_id: number;
  display_name: string;
  primary_email: string | null;
  primary_phone: string | null;
  property_name: string | null;
  conversation_id: string | null;
}

export interface SearchConversationItem {
  id: string;
  contact_id: number;
  last_message_at: string | null;
  last_message_channel: string | null;
  message_preview: string;
  match_count: number;
  matched_message_id: string;
  matched_message_channel?: string | null;
  contact?: ConversationContact | null;
}

export interface SearchConversationsResponse {
  contacts: SearchContactItem[];
  conversations: SearchConversationItem[];
}

export interface MessageResponse {
  message: Message;
}

// Response for GET /api/portal/inbox/conversations/{id}/sms-opt-outs.
// `opted_out_numbers` is the subset of the conversation contact's phone
// numbers that are currently opted out of SMS, as E.164 strings.
export interface SmsOptOutsResponse {
  opted_out_numbers: string[];
}

// Request types
//
// Validation rule (enforced server-side): at_least_one_of(subject, reply_to_message_id)
// and at_least_one_of(to_address, reply_to_message_id). On reply, the server inherits
// subject and recipient from the parent message.
export interface SendEmailRequest {
  conversation_id: string;
  to_address?: string;
  to_name?: string;
  cc_addresses?: string[];
  bcc_addresses?: string[];
  subject?: string;
  content_plain: string;
  content_html: string;
  mailbox_id?: string;
  reply_to_message_id?: string;
  attachment_ids?: string[];
  contact_id?: number;
  // CRM lead the message is about (from the selected link unit). Scopes
  // variable resolution + the lead activity log to that lead; the server
  // falls back to recipient-address match, then the contact's newest lead.
  lead_id?: number;
}

export interface SendSmsRequest {
  conversation_id: string;
  to_number: string;
  content: string;
  phone_number_id?: string;
  // See SendEmailRequest.lead_id.
  lead_id?: number;
  // CRM-1288: single pre-uploaded image sent as MMS media.
  attachment_ids?: string[];
}

export interface SendInAppMessageRequest {
  conversation_id: string;
  content_plain: string;
  content_html?: string;
}

// A BoomScreen application a chat message can be routed to.
export interface EligibleApplication {
  application_id: number;
  address_label: string;
  status: string;
  application_updated_at: string;
}

export interface SendChatRequest {
  conversation_id: string;
  application_id: number;
  content: string;
  client_message_id?: string; // idempotency key
  attachment_ids?: string[];
  // Applicant the agent picked to receive the chat-to-SMS mirror. Omitted → the
  // server falls back to the application's primary applicant.
  recipient_applicant_id?: number;
}

export interface CreateConversationRequest {
  contact_id?: number;
  name?: string;
  email?: string;
  phone?: string;
}

export interface CreateManualNoteRequest {
  conversation_id: string;
  type: string;
  date: string;
  content_plain: string;
  content_html?: string;
}

export interface ReplyTarget {
  messageId: string;
  fromName: string;
  fromAddress: string;
  preview: string;
}

// Manual EN<->ES translation (CRM-1092). `targetLanguage` is the destination
// only — no server/LLM-side detection; see detect-incoming-language.ts.
export type TranslationLanguage = "en" | "es";

// Mirrors communication-service's TranslateDto TEXT_MAX so we can reject
// too-long text client-side instead of dead-ending on a generic 400 toast.
export const TRANSLATE_TEXT_MAX_LENGTH = 20_000;

export interface TranslateRequest {
  text: string;
  // Email-only: translates the subject alongside the body in the same call.
  subject?: string;
  targetLanguage: TranslationLanguage;
}

export interface TranslateResponse {
  translatedText: string;
  translatedSubject?: string;
}
